import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, OverlayTrigger, Popover } from 'react-bootstrap';
import { ElevenLabsClient } from 'elevenlabs';
import MicRecorder from './MicRecorder';
import { Testimonials } from './Testimonials';
import VoiceControl from './VoiceControl';
import { stockVoices, getFirstStockVoice } from './StockVoices';
import { idbGet, idbSet, idbRemove } from './idbStorage';

const VOICES_KEY = 'voices';

// Collect all chunks from an ElevenLabs audio stream.
// Uses the ReadableStream reader API instead of `for await...of` so it works on
// browsers that don't implement Symbol.asyncIterator on ReadableStream (older
// Chrome/Edge < 124, Firefox). Falls back to async iteration for any stream
// type that supports it.
const streamToChunks = async (stream) => {
  const chunks = [];
  if (stream && typeof stream.getReader === 'function') {
    const reader = stream.getReader();
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }
    } finally {
      reader.releaseLock();
    }
  } else {
    for await (const chunk of stream) {
      chunks.push(chunk);
    }
  }
  return chunks;
};

// Utility function to shuffle an array with a seed for consistent randomization
const shuffleArray = (array) => {
  // Get the node number from localStorage to use as seed
  const nodeNumber = localStorage.getItem("nn-node");
  
  if (!nodeNumber || array.length === 0) {
    return array;
  }
  
  // Create a copy of the array to avoid mutating the original
  const shuffled = [...array];
  
  // Convert node number to a numeric seed
  const seed = nodeNumber.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // Better seeded random number generator using xorshift
  let state = seed;
  const seededRandom = () => {
    state ^= state << 13;
    state ^= state >> 17;
    state ^= state << 5;
    return (state >>> 0) / 4294967295; // Convert to 0-1 range
  };
  
  // Fisher-Yates shuffle with seeded random
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

const VoiceCloneCreatorOptions = (props) => {
  // -----------------------
  // Common API and client setup
  // -----------------------
  // Supply your API key securely (e.g., via an environment variable)
  const apiKey = props.elevenlabsKeys[0]
  const client = useMemo(() => new ElevenLabsClient({ apiKey }), [apiKey]);

  // -----------------------
  // Shared voices state for synthesis dropdown. If you want more default voices, put them in here.
  // -----------------------
  const [voices, setVoices] = useState([
    //{ id: 'YwJoWlHMPeElW7r8zaSX', name: 'Roger' },
  ]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');

  // -----------------------
  // Section 1: Voice Clone Creation states
  // -----------------------
  const [selectedSource, setSelectedSource] = useState('record'); // 'record' or 'upload'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [cloneResult, setCloneResult] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [audioURL, setAudioURL] = useState(null);

  // NEW: Stock voice selection states
  const [useStockVoice, setUseStockVoice] = useState(false);
  const [selectedStockVoice, setSelectedStockVoice] = useState(null);
  const [showStockVoices, setShowStockVoices] = useState(false);
  const [voiceSection, setVoiceSection] = useState(null);

  // NEW: Voice Changer states
  const [showVoiceChanger, setShowVoiceChanger] = useState(false);
  const [voiceChangerSource, setVoiceChangerSource] = useState('reuse'); // 'reuse' or 'record'
  const [voiceChangerAudioURL, setVoiceChangerAudioURL] = useState(null);
  const [selectedVoiceChangerVoice, setSelectedVoiceChangerVoice] = useState(null);
  const [voiceChangerRecordings, setVoiceChangerRecordings] = useState([]);
  const [selectedVoiceChangerRecording, setSelectedVoiceChangerRecording] = useState(null);
  const [voiceChangerResult, setVoiceChangerResult] = useState('');
  const [voiceChangerLoading, setVoiceChangerLoading] = useState(false);
  const [voiceChangerInputs, setVoiceChangerInputs] = useState([]);
  const [voiceChangerCategory, setVoiceChangerCategory] = useState('Characters');

  const [showScroll, setShowScroll] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [voiceChangerOutputs, setVoiceChangerOutputs] = useState([]);
  const [synthesisVisible, setSynthesisVisible] = useState(false);
  const [synthesisCompleted, setSynthesisCompleted] = useState(false);
  const [submittedToSlideshow, setSubmittedToSlideshow] = useState(null);
  const [mp3CacheBuster, setMp3CacheBuster] = useState(Date.now());
  const [clearConfirm, setClearConfirm] = useState(false);

  // -----------------------
  // Section 2: Synthesis states
  // -----------------------
  // For text-to-speech and speech-to-speech synthesis
  const [audioUrlSynth, setAudioUrlSynth] = useState([]);
  const [synthLoading, setSynthLoading] = useState(false);
  const [error, setError] = useState('');

  // NEW: Synthesis input method state: "text", "record", or "file"
  const [synthInputSource, setSynthInputSource] = useState('text');
  const [synthRecordingURL, setSynthRecordingURL] = useState(null); // from microphone
  const [synthUploadedFile, setSynthUploadedFile] = useState(null);   // from local file
  const [synthesisText, setSynthesisText] = useState('');

  // Mirror of the persisted "voices" record so we don't re-parse on every setter.
  // Mutated in-place by the save* helpers, then flushed via the IDB coalescer.
  const voicesDataRef = useRef({});
  // Gate writes until the initial async load has completed. Without this, an
  // early save would overwrite a partial in-memory copy on top of whatever
  // the load eventually returns.
  const voicesLoadedRef = useRef(false);

  const persistVoicesData = () => {
    if (!voicesLoadedRef.current) return;
    idbSet(VOICES_KEY, voicesDataRef.current).catch((e) =>
      console.error("Failed to save voices:", e)
    );
  };

  // Set default selected voice for synthesis if none is selected.
  useEffect(() => {
    if (voices.length > 0 && !selectedVoiceId) {
      setSelectedVoiceId(voices[0].id);
    }
  }, [voices, selectedVoiceId]);

  useEffect(() => {
    const checkScroll = () => {
      const scrollPosition = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const isScrollable = documentHeight > windowHeight + 100;
      const shouldShow = isScrollable && (scrollPosition > 200 || scrollPosition + windowHeight < documentHeight - 200);
      setShowScroll(shouldShow);
      setShowScrollBottom(shouldShow);
    };
    window.addEventListener('scroll', checkScroll);
    checkScroll();
    return () => window.removeEventListener('scroll', checkScroll);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const scrollBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load all voice-related data from IndexedDB
      let parsedData = null;
      try {
        parsedData = await idbGet(VOICES_KEY);
      } catch (e) {
        console.error("Failed to load voices:", e);
      }
      if (cancelled) return;
      voicesDataRef.current = parsedData || {};
      voicesLoadedRef.current = true;
      if (!parsedData) return;

      // Load voices
      if (parsedData.voices) {
        setVoices(parsedData.voices);
        if (parsedData.voices.length > 0) {
          setSynthesisVisible(true);
        }
      }
      
      // Load stock voices preference
      if (parsedData.showStockVoices !== undefined) {
        setShowStockVoices(parsedData.showStockVoices);
      }
      
      // Load selected stock voice
      if (parsedData.selectedStockVoice) {
        setSelectedStockVoice(parsedData.selectedStockVoice);
        setUseStockVoice(true);
        setSelectedVoiceId(parsedData.selectedStockVoice.id);
        setSynthesisVisible(true);
      }
      
      // Load synthesis completed state
      if (parsedData.synthesisCompleted) {
        setSynthesisCompleted(parsedData.synthesisCompleted);
        if (parsedData.synthesisCompleted) {
          setShowVoiceChanger(true);
        }
      }
      
      // Load and process synthesized voices
      if (parsedData.synth) {
        let out = []
        for (let i = 0; i < parsedData.synth.length; i++) {
          const base64_blob = parsedData.synth[i]['audio_blob'];
          if (!base64_blob) continue;

          const url = base64ToAudioURL(base64_blob);

        // Add url to array of synth urls
          const new_synth = {'id': parsedData.synth[i]['id'], 'name': parsedData.synth[i]['name'], 'url': url};
        out.push(new_synth);
        console.log(out);
      }
      setAudioUrlSynth(out);
        
        // Load voice changer inputs and outputs separately
        if (parsedData.voiceChangerInputs) {
          // Process input recordings from base64 blobs
          const processedInputs = processAudioRecordingsFromStorage(parsedData.voiceChangerInputs);
          setVoiceChangerInputs(processedInputs);
        }
        
        if (parsedData.voiceChangerOutputs) {
          // Process output recordings from base64 blobs
          const processedOutputs = processAudioRecordingsFromStorage(parsedData.voiceChangerOutputs);
          setVoiceChangerOutputs(processedOutputs);
        }
        
        // Combine inputs and outputs for display if either exists
        if (parsedData.voiceChangerInputs || parsedData.voiceChangerOutputs) {
          setShowVoiceChanger(true);
          const allRecordings = [
            ...(parsedData.voiceChangerInputs || []),
            ...(parsedData.voiceChangerOutputs || [])
          ];
          setVoiceChangerRecordings(allRecordings);
          
          // Auto-select the first recording if none is selected
          if (allRecordings.length > 0 && !selectedVoiceChangerRecording) {
            setSelectedVoiceChangerRecording(allRecordings[0]);
          }
          
          // Auto-select the first target voice if none is selected
          if (stockVoices.length > 0 && !selectedVoiceChangerVoice) {
            setSelectedVoiceChangerVoice(getFirstStockVoice());
          }
        }
        
        // Show voice changer if synthesis has been completed (even without recordings)
        if (synthesisCompleted) {
          setShowVoiceChanger(true);
          // Auto-select the first target voice if none is selected
          if (stockVoices.length > 0 && !selectedVoiceChangerVoice) {
            setSelectedVoiceChangerVoice(getFirstStockVoice());
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (props.activeTab === "voice-cloning") {
      console.log("voice tab selected")
      if (!synthesisText) {
        setSynthesisText(props.voiceOver || props.elevatorPitch)
      }
    }
  }, [props.activeTab, props.voiceOver]);

  function getVoiceNameFromId(id) {
    const voice = voices.find((v) => v.id === id);
    return voice ? voice.name : '';
  }

  // -----------------------
  // Handlers for Clone Creation
  // -----------------------
  const handleFileChange = (e) => {
    setUploadedFile(e.target.files[0]);
  };

  const handleSourceChange = (e) => {
    setSelectedSource(e.target.value);
    // Reset any previous audio sample.
    setAudioURL(null);
    setUploadedFile(null);
  };

  const saveVoicesToLocalStorage = (new_voices) => {
    voicesDataRef.current = { ...voicesDataRef.current, voices: new_voices };
    persistVoicesData();
  }

  const saveSynthToLocalStorage = (new_synth) => {
    voicesDataRef.current = { ...voicesDataRef.current, synth: new_synth };
    persistVoicesData();
  }

  const saveShowStockVoicesToLocalStorage = (showStock) => {
    voicesDataRef.current = { ...voicesDataRef.current, showStockVoices: showStock };
    persistVoicesData();
  }

  const saveSelectedStockVoiceToLocalStorage = (stockVoice) => {
    voicesDataRef.current = { ...voicesDataRef.current, selectedStockVoice: stockVoice };
    persistVoicesData();
  }

  const saveSynthesisCompletedToLocalStorage = (completed) => {
    voicesDataRef.current = { ...voicesDataRef.current, synthesisCompleted: completed };
    persistVoicesData();
  }

  const saveVoiceChangerInputsToLocalStorage = (inputRecordings) => {
    voicesDataRef.current = { ...voicesDataRef.current, voiceChangerInputs: inputRecordings };
    persistVoicesData();
  }

  const saveVoiceChangerOutputsToLocalStorage = (outputRecordings) => {
    voicesDataRef.current = { ...voicesDataRef.current, voiceChangerOutputs: outputRecordings };
    persistVoicesData();
  }

  const handleHideStockVoices = () => {
    setShowStockVoices(false);
    saveShowStockVoicesToLocalStorage(false);
  }

  // NEW: Handler for stock voice selection
  const handleStockVoiceSelect = (voice) => {
    setSelectedStockVoice(voice);
    setUseStockVoice(true);
    // Don't add stock voices to the voices list - they should remain separate
    setSelectedVoiceId(voice.id);
    saveSelectedStockVoiceToLocalStorage(voice);
    
    // Scroll to the voice synthesis section
    setTimeout(() => {
      const synthesisSection = document.querySelector('[style*="visibility: visible"]');
      if (synthesisSection) {
        synthesisSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // NEW: Handler for cloned voice selection
  const handleClonedVoiceSelect = (voiceId) => {
    setSelectedVoiceId(voiceId);
    // Deselect stock voice when selecting a cloned voice
    setSelectedStockVoice(null);
    setUseStockVoice(false);
    saveSelectedStockVoiceToLocalStorage(null);
  };

  // NEW: Handler for voice changer voice selection
  const handleVoiceChangerVoiceSelect = (voice) => {
    setSelectedVoiceChangerVoice(voice);
  };

  // NEW: Handler for submitting to slideshow
  const handleSubmitToSlideshow = async (recording) => {
    console.log('Submitting to slideshow:', recording);
    setSubmittedToSlideshow(recording.id);
    
    try {
      // Fetch the audio blob from the recording URL
      const response = await fetch(recording.url);
      const audioBlob = await response.blob();
      
      // Create FormData to send the audio blob
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_recording.mp3');
      formData.append('recordingId', recording.id);
      formData.append('recordingName', recording.name);
      formData.append('originalVoice', recording.originalVoice);
      formData.append('targetVoice', recording.targetVoice);
      formData.append('password', localStorage.getItem("frontend-password"));
      formData.append('student_id', localStorage.getItem("nn-node"));
      formData.append('workshop_id', props.workshopId);
      
      // Make POST request to submit voice to slideshow
      const submitResponse = await fetch('/submit-voice-to-slideshow', {
        method: 'POST',
        body: formData
      });
      
      if (!submitResponse.ok) {
        throw new Error(`HTTP error! status: ${submitResponse.status}`);
      }
      
      const result = await submitResponse.json();
      console.log('Voice submitted successfully:', result);
      
    } catch (error) {
      console.error('Error submitting voice to slideshow:', error);
      // Reset the submitted state if there was an error
      setSubmittedToSlideshow(null);
    }
  };

  // NEW: Handler for voice changer button
  const handleVoiceChanger = async () => {
    if (!selectedVoiceChangerVoice || !selectedVoiceChangerRecording) {
      alert('Please select both a recording and a target voice.');
      return;
    }

    setVoiceChangerLoading(true);
    setVoiceChangerResult('');

    console.log(selectedVoiceChangerVoice);

    try {
      // Fetch the audio blob from the recording URL
      const response = await fetch(selectedVoiceChangerRecording.url);
      const audioBlob = await response.blob();
      
      // Convert blob to File object for the API
      const audioFile = new File([audioBlob], 'voice_changer_input.mp3', { type: 'audio/mpeg' });

      // Perform speech-to-speech conversion
      const conversionStream = await client.speechToSpeech.convert(selectedVoiceChangerVoice.id, {
        audio: audioFile,
        outputFormat: "mp3_44100_128",
        modelId: "eleven_english_sts_v2"
      });

      // Collect the audio chunks
      const chunks = await streamToChunks(conversionStream);

      // Create the final audio blob and URL
      const convertedAudioBlob = new Blob(chunks, { type: 'audio/mpeg' });
      const convertedAudioURL = URL.createObjectURL(convertedAudioBlob);

      // Convert to base64 for storage
      const convertedAudioBase64 = await blobToBase64(convertedAudioBlob);

      // Add the converted audio to the recordings list
      const newConvertedRecording = {
        id: `converted-${Date.now()}`,
        name: `${voiceChangerOutputs.length + 1}`,
        url: convertedAudioURL,
        audio_blob: convertedAudioBase64,
        originalVoice: selectedVoiceChangerRecording.name,
        targetVoice: selectedVoiceChangerVoice.name
      };

      setVoiceChangerOutputs(prev => {
        const updated = [...prev, newConvertedRecording];
        saveVoiceChangerOutputsToLocalStorage(updated);
        return updated;
      });
      
      // Update combined recordings list
      setVoiceChangerRecordings(prev => {
        const updated = [...prev, newConvertedRecording];
        return updated;
      });
      
      setSelectedVoiceChangerRecording(newConvertedRecording);
      
      setVoiceChangerResult(`Voice changed successfully! Converted from "${selectedVoiceChangerRecording.name}" to "${selectedVoiceChangerVoice.name}"`);
      
    } catch (error) {
      console.error('Error in voice changer:', error);
      setVoiceChangerResult(`Error changing voice: ${error.message}`);
    } finally {
      setVoiceChangerLoading(false);
    }
  };

  // NEW: Handler to add new recording to voice changer list
  const handleAddVoiceChangerRecording = async (newAudioURL) => {
    // Only add if this is a new recording (not already in the list)
    const isNewRecording = !voiceChangerInputs.some(recording => recording.url === newAudioURL);
    
    if (isNewRecording && newAudioURL) {
      // Convert audio URL to base64 blob for storage
      const audioBase64 = await audioURLToBase64(newAudioURL);

      const newRecording = {
        id: `recording-${Date.now()}`,
        name: `${voiceChangerInputs.length + 1}`,
        url: newAudioURL,
        audio_blob: audioBase64
      };
      
      setVoiceChangerInputs(prev => {
        const updated = [...prev, newRecording];
        saveVoiceChangerInputsToLocalStorage(updated);
        return updated;
      });
      
      // Update combined recordings list
      setVoiceChangerRecordings(prev => {
        const updated = [...prev, newRecording];
        return updated;
      });
      
      // Auto-select if this is the only recording
      if (voiceChangerInputs.length === 0) {
        setSelectedVoiceChangerRecording(newRecording);
      }
      // Auto-select new recording if there are multiple recordings
      if (voiceChangerInputs.length > 0) {
        setSelectedVoiceChangerRecording(newRecording);
      }
    }
  };

  const handleCloneSubmit = async (e) => {
    e.preventDefault();
    
    // Generate auto-incrementing voice name
    const voiceNumber = voices.length + 1;
    const voiceName = `Voice Clone ${voiceNumber}`;
    
    let fileForUpload = null;
    let cloneAudioURL = null; // Store the audio URL for voice changer
    let cloneAudioBlob = null; // Store the audio blob for voice changer
    
    if (selectedSource === 'record') {
      if (!audioURL) {
        alert('Please record your voice first.');
        return;
      }
      try {
        const res = await fetch(audioURL);
        const blob = await res.blob();
        fileForUpload = new File([blob], 'voice_sample.webm', { type: blob.type });
        cloneAudioURL = audioURL; // Store the original audio URL
        cloneAudioBlob = blob; // Store the original audio blob
      } catch (error) {
        alert('Error processing recorded audio.');
        console.error('Error fetching blob from audioURL:', error);
        return;
      }
    } else {
      if (!uploadedFile) {
        alert('Please upload an audio file.');
        return;
      }
      fileForUpload = uploadedFile;
      // For uploaded files, create a URL and blob for voice changer
      cloneAudioURL = URL.createObjectURL(uploadedFile);
      cloneAudioBlob = uploadedFile;
    }
    setCloneLoading(true);
    setCloneResult('');
    try {
      const response = await client.voices.add({
        files: [fileForUpload],
        name: voiceName,
      });
      // Create a new voice entry using the returned voice_id and generated voiceName.
      const newVoiceEntry = { id: response.voice_id, name: voiceName };
      console.log("New voice entry:", newVoiceEntry);
      // Update the shared voices state so that the new voice appears in the synthesis dropdown.
      setVoices(prevVoices => {
        const updated = [...prevVoices, newVoiceEntry];
        saveVoicesToLocalStorage(updated);
        return updated;
      });
      
      // Automatically add the clone audio to voice changer recordings
      if (cloneAudioURL && cloneAudioBlob) {
        const audioBase64 = await blobToBase64(cloneAudioBlob);
        
        const newVoiceChangerRecording = {
          id: `clone-recording-${Date.now()}`,
          name: `${voiceNumber}`,
          url: cloneAudioURL,
          audio_blob: audioBase64
        };
        
        setVoiceChangerInputs(prev => {
          const updated = [...prev, newVoiceChangerRecording];
          saveVoiceChangerInputsToLocalStorage(updated);
          return updated;
        });
        
        // Update combined recordings list
        setVoiceChangerRecordings(prev => {
          const updated = [...prev, newVoiceChangerRecording];
          return updated;
        });
        
        // Auto-select the new recording for voice changer
        setSelectedVoiceChangerRecording(newVoiceChangerRecording);
        
        // Auto-select the first target voice if none is selected
        if (stockVoices.length > 0 && !selectedVoiceChangerVoice) {
          setSelectedVoiceChangerVoice(getFirstStockVoice());
        }
      }
      
      setCloneResult(`Voice clone created successfully: ${JSON.stringify(response, null, 2)}`);
      setSelectedVoiceId(response.voice_id);
    } catch (error) {
      setCloneResult(`Error creating voice clone: ${error.message}`);
    } finally {
      setCloneLoading(false);
    }
  };

  // -----------------------
  // Handlers for Synthesis
  // -----------------------
  const handleVoiceDropdownChange = (e) => {
    setSelectedVoiceId(e.target.value);
  };

  const handleSynthesisSourceChange = (e) => {
    setSynthInputSource(e.target.value);
    // Reset any previous synthesis sample.
    // setSynthesisText('');
    setSynthRecordingURL(null);
    setSynthUploadedFile(null);
  };

  const handleSynthFileChange = (e) => {
    setSynthUploadedFile(e.target.files[0]);
  };

  const handleSynthesize = async () => {
    if (!selectedVoiceId) {
      alert('Please select a voice.');
      return;
    }
    setSynthLoading(true);
    try {
      let audioResponse;
      if (synthInputSource === 'text') {
        if (!synthesisText) {
          alert('Please enter text to synthesize.');
          setSynthLoading(false);
          return;
        }
        const stream = await client.textToSpeech.convertAsStream(selectedVoiceId, {
          text: synthesisText,
          model_id: 'eleven_multilingual_v2',
        });
        const chunks = await streamToChunks(stream);
        audioResponse = new Blob(chunks, { type: 'audio/mpeg' });
      } else if (synthInputSource === 'record') {
        if (!synthRecordingURL) {
          alert('Please record your synthesis audio first.');
          setSynthLoading(false);
          return;
        }

        // For speech-to-speech conversion using recorded input. 
        // This was tricky as the webm format failed without much feedback
        // also, the conversionStream comes back in chunks
        const res = await fetch(synthRecordingURL);
        const blob = await res.blob();
        const fileForSynthesis = new File([blob], 'synth_input.mp3', { type: 'audio/mpeg' });
        const conversionStream = await client.speechToSpeech.convert(selectedVoiceId, {
          audio: fileForSynthesis,
          outputFormat: "mp3_44100_128",
          modelId: "eleven_multilingual_sts_v2"
        });
        const conversionChunks = await streamToChunks(conversionStream);
        audioResponse = new Blob(conversionChunks, { type: 'audio/mpeg' });
      } else if (synthInputSource === 'file') {
        if (!synthUploadedFile) {
          alert('Please select a local MP3 file for synthesis.');
          setSynthLoading(false);
          return;
        }
        const conversionStream = await client.speechToSpeech.convert(selectedVoiceId, {
          audio: synthUploadedFile,
          outputFormat: "mp3_44100_128",
          modelId: "eleven_multilingual_sts_v2"
        });
        const conversionChunks = await streamToChunks(conversionStream);
        audioResponse = new Blob(conversionChunks, { type: 'audio/mpeg' });
      }
      const url = URL.createObjectURL(audioResponse);

      // Get the correct voice name based on whether it's a stock voice or cloned voice
      const voiceName = useStockVoice ? selectedStockVoice?.name : getVoiceNameFromId(selectedVoiceId);

      // Add url to array of synth urls
      const new_synth = [...audioUrlSynth, {'id': selectedVoiceId, 'name': voiceName, 'url': url}];
      setAudioUrlSynth(new_synth);

      const audioBase64 = await blobToBase64(audioResponse);

      const save_blob = {
        'id': selectedVoiceId,
        'name': voiceName,
        'audio_blob': audioBase64
      }
      
      // Append the new synth to the existing synth array
      const existingSynth = voicesDataRef.current.synth || [];
      saveSynthToLocalStorage([...existingSynth, save_blob]);
      
      // Mark synthesis as completed and show voice changer section
      setSynthesisCompleted(true);
      saveSynthesisCompletedToLocalStorage(true);
      setShowVoiceChanger(true);
      
      // Auto-select the first target voice
      if (stockVoices.length > 0) {
        setSelectedVoiceChangerVoice(getFirstStockVoice());
      }
    } catch (err) {
      setError(err.message);
      console.error('Error synthesizing speech:', err);
    } finally {
      setSynthLoading(false);
    }
  };

  function handleVoiceClearAll() {
    if (clearConfirm) {
      setVoices([]);
      setSelectedVoiceId('');
      setAudioURL(null);
      setUploadedFile(null);
      setCloneResult('');
      setUseStockVoice(false);
      setSelectedStockVoice(null);
      setShowStockVoices(false);
      setVoiceSection(null);
      setShowVoiceChanger(false);
      setVoiceChangerInputs([]);
      setVoiceChangerOutputs([]);
      setVoiceChangerRecordings([]);
      setSelectedVoiceChangerRecording(null);
      setSelectedVoiceChangerVoice(null);
      setSynthesisVisible(false);
      setSynthesisCompleted(false);
      setSynthesisText('');
      setAudioUrlSynth([]);
      setSubmittedToSlideshow(null);
      setError('');
      voicesDataRef.current = {};
      idbRemove(VOICES_KEY).catch(() => {});
      setClearConfirm(false);
      return;
    }
    setClearConfirm(true);
    setTimeout(() => {
      setClearConfirm(false);
    }, 5000);
  }

  let cloneVoiceButton = <></>

  if (audioURL || uploadedFile) {
    cloneVoiceButton = (
      <>
        <br />
        <Button variant="success" type="submit" className="mt-4 btn-voice-clone" disabled={cloneLoading} style={{ height: '50px', width: '200px' }}>
          {cloneLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Cloning...
            </>
          ) : 'Clone Voice'}
        </Button>
      </>
    )
  }

  let audioSynthOut = []

  if (audioUrlSynth) {
    audioSynthOut = audioUrlSynth.map((data, index) => {
      return (
        <div key={index} className="mt-3">
          <div className="d-flex align-items-center gap-3">
            <Form.Label className="mb-0" style={{ minWidth: '120px' }}>{data.name}</Form.Label>
            <audio controls src={data.url} className="flex-grow-1 voice-cloning-audio" />
          </div>
        </div>
      )
    })
  }

  // Update synthesis visibility when voices change
  useEffect(() => {
    if (voices.length > 0 || (useStockVoice && selectedStockVoice)) {
      setSynthesisVisible(true);
    } else {
      setSynthesisVisible(false);
    }
  }, [voices, useStockVoice, selectedStockVoice]);

  // -----------------------
  // Utility functions for audio blob handling
  // -----------------------
  const audioURLToBase64 = async (audioURL) => {
    const response = await fetch(audioURL);
    const audioBlob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(audioBlob);
    });
  };

  const blobToBase64 = async (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const base64ToBlob = (base64Data) => {
    // Strip off the data URL prefix if needed
    const byteString = atob(base64Data.split(',')[1]);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([byteArray]);
  };

  const base64ToAudioURL = (base64Data) => {
    const blob = base64ToBlob(base64Data);
    return URL.createObjectURL(blob);
  };

  const processAudioRecordingsFromStorage = (recordings) => {
    return recordings.map(recording => {
      if (recording.audio_blob) {
        return {
          ...recording,
          url: base64ToAudioURL(recording.audio_blob)
        };
      }
      return recording;
    });
  };

  // -----------------------
  // Render Combined Component
  // -----------------------
  return (
    <Container className="mt-1 mb-0">
      <Row className="mb-0">
        <Col>
          <Card style={{ borderColor: "#d2c4ee" }}>
            <Card.Header as="h4" style={{ backgroundColor: "#e6dcf8", borderColor: "#d2c4ee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              Generate a Voice Clone with AI
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                <Button size="sm" className="btn-clear-voice" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={clearConfirm ? "danger" : "outline-secondary"} onClick={handleVoiceClearAll}>
                  {clearConfirm ? "Confirm Clear" : "Clear All"}
                </Button>
              </div>
            </Card.Header>
            <Card.Body style={{ backgroundColor: "#fbfaff" }}>

              {/* Sub-Card 1: Create a Voice */}
              <Card className="mb-4" style={{ borderColor: "#d2c4ee" }}>
                <Card.Header as="h5" style={{ backgroundColor: "#f1f1f3", borderColor: "#d2c4ee" }}>
                  Create a Voice
                </Card.Header>
                <Card.Body style={{ backgroundColor: "#fbfaff" }}>
                  <p style={{ fontWeight: "bold", marginBottom: "8px" }}>Select:</p>
                  <div className="d-flex gap-2 mb-3 voice-section-toggle">
                    <Button
                      className="btn-voice-section"
                      variant={voiceSection === 'record' ? 'primary' : 'outline-secondary'}
                      onClick={() => setVoiceSection('record')}
                    >
                      Clone your voice
                    </Button>
                    <Button
                      className="btn-voice-section"
                      variant={voiceSection === 'stock' ? 'primary' : 'outline-secondary'}
                      onClick={() => { setVoiceSection('stock'); setShowStockVoices(true); }}
                    >
                      Select a stock voice
                    </Button>
                  </div>

                  {voiceSection === 'record' && (
                  <>
                  <Form onSubmit={handleCloneSubmit}>
                    <Row>
                      <Col md={12}>
                        <h5>Record Your Own Voice</h5>
                        <Form.Group controlId="source">
                          <div>
                            {selectedSource === "record" && <Testimonials />}
                          </div>
                        </Form.Group>
                        {selectedSource === 'record' ? (
                          <>
                            <MicRecorder onAudioURLChange={setAudioURL} showStockButton={!showStockVoices} onShowStock={() => {
                              setShowStockVoices(true);
                              saveShowStockVoicesToLocalStorage(true);
                            }} />
                            {audioURL && <audio controls src={audioURL} className="voice-cloning-audio" />}
                          </>
                        ) : (
                          <Form.Group controlId="fileUpload" className="mt-3">
                            <Form.Label>Upload MP3 File</Form.Label>
                            <Form.Control
                              type="file"
                              accept=".mp3,audio/mpeg"
                              onChange={handleFileChange}
                              required
                            />
                            {uploadedFile && (
                              <Form.Text className="text-muted">{uploadedFile.name}</Form.Text>
                            )}
                          </Form.Group>
                        )}

                        {cloneVoiceButton}

                        {voices.length > 0 && (
                          <div className="mt-4">
                            <h5>Your Cloned Voices</h5>
                            <p className="text-muted mb-3">Click on a voice to select it for use</p>
                            <div className="d-flex flex-column gap-3">
                              {voices.map((voice) => (
                                <div 
                                  key={voice.id} 
                                  className={`p-3 border rounded ${selectedVoiceId === voice.id && !useStockVoice ? 'border-primary bg-light' : 'border-secondary'}`}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleClonedVoiceSelect(voice.id)}
                                >
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                      <h6 className="fw-bold mb-1">{voice.name}</h6>
                                      <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                        Your cloned voice
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Col>

                    </Row>
                  </Form>
                  {(cloneResult && cloneResult.startsWith('Error')) && (
                    <Alert variant={cloneResult.startsWith('Error') ? 'danger' : 'success'} className="mt-3">
                      <pre className="mb-0">{cloneResult}</pre>
                    </Alert>
                  )}
                  </>
                  )}

                  {voiceSection === 'stock' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Select a Stock Voice</h5>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {shuffleArray(stockVoices.filter(voice => voice.isNarrator)).map((voice) => (
                          <div 
                            key={voice.id} 
                            className={`p-2 border rounded ${selectedStockVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStockVoiceSelect(voice)}
                          >
                            <VoiceControl
                              voice={voice}
                              variant="outline-secondary"
                              size="sm"
                              onPlay={() => handleStockVoiceSelect(voice)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Sub-Card 2: Voice Synthesis */}
              {synthesisVisible && (
                <Card className="mb-4" style={{ borderColor: "#d2c4ee" }}>
                  <Card.Header as="h5" style={{ backgroundColor: "#f1f1f3", borderColor: "#d2c4ee" }}>
                    Voice Synthesis{props.workshopConfig.synthesisCardSubtitle && <> - <span style={{ fontSize: '0.75em', fontWeight: 'normal' }}>{props.workshopConfig.synthesisCardSubtitle}</span></>}
                  </Card.Header>
                  <Card.Body style={{ backgroundColor: "#fbfaff" }}>
                    <Form>
                      {selectedVoiceId && (
                        <div className="mb-3" style={{
                          border: '2px solid #f05f40',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          padding: '8px 12px',
                          display: 'inline-block'
                        }}>
                          <p className="mb-0" style={{ color: 'black' }}>
                            <strong>Selected Voice:</strong> {useStockVoice ? selectedStockVoice?.name : getVoiceNameFromId(selectedVoiceId)}
                          </p>
                        </div>
                      )}
                      {synthInputSource === 'text' && (
                        <Form.Group controlId="textInput" className="mt-3">
                          {props.workshopConfig.synthesisHelper && !synthesisText && props.workshopConfig.synthesisHelper(props.switchTab, props.setPendingGptPrompt)}
                          <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder={props.workshopConfig.placeholder}
                            value={synthesisText}
                            onChange={(e) => setSynthesisText(e.target.value)}
                            maxLength={1000}
                          />
                        </Form.Group>
                      )}
                      {synthInputSource === 'record' && (
                        <Form.Group controlId="synthMicInput" className="mt-3">
                          <Form.Label>Record Input</Form.Label>
                          <MicRecorder onAudioURLChange={setSynthRecordingURL} />
                          {synthRecordingURL && <audio controls src={synthRecordingURL} className="voice-cloning-audio" />}
                        </Form.Group>
                      )}
                      {synthInputSource === 'file' && (
                        <Form.Group controlId="synthFileInput" className="mt-3">
                          <Form.Label>Select Local MP3 File</Form.Label>
                          <Form.Control
                            type="file"
                            accept=".mp3,audio/mpeg"
                            onChange={handleSynthFileChange}
                          />
                          {synthUploadedFile && (
                            <Form.Text className="text-muted">{synthUploadedFile.name}</Form.Text>
                          )}
                        </Form.Group>
                      )}
                      <Button variant="primary" onClick={handleSynthesize} className="mt-4 btn-voice-synthesize" disabled={synthLoading}>
                        {synthLoading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Synthesizing...
                          </>
                        ) : 'Synthesize and Play'}
                      </Button>
                    </Form>
                    {audioSynthOut}
                    {error && <div>{error}</div>}
                  </Card.Body>
                </Card>
              )}

              {/* Sub-Card 3: Voice Changer */}
              {showVoiceChanger && (
                <Card className="mb-4" style={{ borderColor: "#d2c4ee" }}>
                  <Card.Header as="h5" style={{ backgroundColor: "#f1f1f3", borderColor: "#d2c4ee" }}>
                    Voice Changer
                  </Card.Header>
                  <Card.Body style={{ backgroundColor: "#fbfaff" }}>
                    <Row className="d-flex" style={{ alignItems: "stretch" }}>
                      <Col md={6} className="d-flex">
                        <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "15px", width: "100%", display: "flex", flexDirection: "column" }}>
                          <h5>Select Input Voice</h5>
                          
                          {voiceChangerInputs.length > 0 && (
                            <div className="mb-3">
                              <div className="d-flex flex-column gap-2">
                                {voiceChangerInputs.map((recording) => (
                                  <div 
                                    key={recording.id}
                                    className={`p-3 border rounded ${selectedVoiceChangerRecording?.id === recording.id ? 'border-primary bg-light' : 'border-light'}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedVoiceChangerRecording(recording)}
                                  >
                                    <div className="d-flex align-items-center gap-3">
                                      <Form.Check
                                        type="radio"
                                        id={`recording-${recording.id}`}
                                        name="voiceChangerRecording"
                                        checked={selectedVoiceChangerRecording?.id === recording.id}
                                        onChange={() => setSelectedVoiceChangerRecording(recording)}
                                        className="mb-0"
                                      />
                                      <Form.Label 
                                        className="mb-0" 
                                        style={{ minWidth: '30px', cursor: 'pointer' }}
                                        onClick={() => setSelectedVoiceChangerRecording(recording)}
                                      >
                                        {recording.name}
                                      </Form.Label>
                                      <audio controls src={recording.url} className="flex-grow-1 voice-cloning-audio" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <hr />
                          <div>
                            <h6>Record New Audio</h6>
                            <MicRecorder onAudioURLChange={handleAddVoiceChangerRecording} />
                          </div>
                        </div>
                      </Col>

                      <Col md={6} className="d-flex">
                        <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "15px", width: "100%", display: "flex", flexDirection: "column" }}>
                          <h5>Select Target Voice</h5>
                          <p className="text-muted mb-3">Choose a voice to change your audio to</p>
                          
                          <Form.Select
                            value={voiceChangerCategory}
                            onChange={(e) => setVoiceChangerCategory(e.target.value)}
                            className="mb-3"
                          >
                            <option value="Characters">Characters</option>
                            <option value="Villains">Villains</option>
                            <option value="Fantasy">Fantasy</option>
                            <option value="Narrators">Narrators</option>
                          </Form.Select>

                          <div className="d-flex flex-column gap-1" style={{ maxHeight: '220px', overflowY: 'auto', flex: "1", border: '1px solid #eee', borderRadius: '6px', padding: '8px' }}>
                            {shuffleArray(stockVoices.filter(voice => voice.category === voiceChangerCategory)).map((voice) => (
                              <div 
                                key={voice.id} 
                                className={`p-2 border rounded ${selectedVoiceChangerVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleVoiceChangerVoiceSelect(voice)}
                              >
                                <VoiceControl
                                  voice={voice}
                                  variant="outline-secondary"
                                  size="sm"
                                  onPlay={() => handleVoiceChangerVoiceSelect(voice)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-start mt-4">
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Popover id="change-voice-tooltip">
                            <Popover.Body>
                              {!selectedVoiceChangerRecording && !selectedVoiceChangerVoice && (
                                <div>Record an audio file and select a target voice to enable voice changing.</div>
                              )}
                              {!selectedVoiceChangerRecording && selectedVoiceChangerVoice && (
                                <div>Record an audio file to enable voice changing.</div>
                              )}
                              {selectedVoiceChangerRecording && !selectedVoiceChangerVoice && (
                                <div>Select a target voice to enable voice changing.</div>
                              )}
                            </Popover.Body>
                          </Popover>
                        }
                        show={(!selectedVoiceChangerVoice || !selectedVoiceChangerRecording) ? undefined : false}
                      >
                        <span>
                          <Button 
                            variant="warning" 
                            onClick={handleVoiceChanger}
                            disabled={!selectedVoiceChangerVoice || !selectedVoiceChangerRecording || voiceChangerLoading}
                            size="lg"
                            className="btn-voice-changer"
                          >
                            {voiceChangerLoading ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Changing Voice...
                              </>
                            ) : 'Change Voice'}
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </div>

                    {voiceChangerOutputs.length > 0 && (
                      <div className="mt-4">
                        <h5>Voice-changed recordings</h5>
                        <div className="d-flex flex-column gap-3">
                          {voiceChangerOutputs.map((recording) => (
                            <div 
                              key={recording.id}
                              className="p-3 border rounded border-success bg-light"
                            >
                              <div className="mb-2 d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1">Recording {recording.name}</h6>
                                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                    Converted from "{recording.originalVoice}" to "{recording.targetVoice}"
                                  </p>
                                </div>
                                {!props.workshopConfig.hideSlideshow && (
                                  <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => handleSubmitToSlideshow(recording)}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    disabled={submittedToSlideshow === recording.id}
                                    className="btn-voice-submit"
                                  >
                                    {submittedToSlideshow === recording.id ? 'Submitted' : 'Submit to slideshow'}
                                  </Button>
                                )}
                              </div>
                              <audio controls src={recording.url} className="w-100 voice-cloning-audio" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {showVoiceChanger && (
                <>
                  <hr style={{ marginTop: "20px" }} />
                  <div className="voice-instructions-box" style={{
                    backgroundColor: "white",
                    fontSize: "large",
                    border: "2px solid #f05f40",
                    borderRadius: "15px",
                    padding: "10px 15px 15px 15px",
                    margin: "15px 0 10px 0",
                    boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
                    width: "50%"
                  }}>
                    <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                      Instructions:
                    </h3>
                    <span style={{ margin: "0", lineHeight: "2.2" }}>
                      {props.workshopConfig.instructionText}{" "}
                      <Button
                        className="btn-nav-link"
                        onClick={() => {
                          const tab = props.workshopConfig.nextStepTab || 'video';
                          props.switchTab(tab);
                          if (props.workshopConfig.nextStepPromptIndex != null && props.setPendingGptPrompt) {
                            props.setPendingGptPrompt(props.workshopConfig.nextStepPromptIndex);
                          }
                        }}
                        style={{
                          margin: "0 5px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          display: "inline",
                          whiteSpace: "nowrap",
                          backgroundColor: "white",
                          borderColor: "#f05f40",
                          color: "#f05f40",
                          verticalAlign: "middle",
                        }}
                      >
                        {props.workshopConfig.nextStepButtonText}
                      </Button>
                      {props.workshopConfig.instructionTextAfter}
                    </span>
                  </div>
                  <hr style={{ marginTop: "15px" }} />
                </>
              )}
            </Card.Body>
            <Card.Footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "0.4rem 0.75rem",
                backgroundColor: "#e6dcf8",
              }}
            >
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                <Button size="sm" className="btn-clear-voice" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={clearConfirm ? "danger" : "outline-secondary"} onClick={handleVoiceClearAll}>
                  {clearConfirm ? "Confirm Clear" : "Clear All"}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <Button
        className="scroll-arrow"
        onClick={scrollTop}
        style={{
          display: showScroll ? "flex" : "none",
          position: "fixed",
          bottom: "80px",
          right: "20px",
          zIndex: "1000",
        }}
        variant="secondary"
      >
        ↑
      </Button>
      <Button
        className="scroll-arrow"
        onClick={scrollBottom}
        style={{
          display: showScrollBottom ? "flex" : "none",
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: "1000",
        }}
        variant="secondary"
      >
        ↓
      </Button>
      
    </Container>
  );
};

export { VoiceCloneCreatorOptions };
